import { Box, Button, Dialog, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useState } from 'react'
import api from '../../../service/api'
import { Icons, attachmentThumbnails, convertToCDNLink, getFileSizeFromBytes } from '../../../utils/utilities'
import TableHeader from '../../common/TableHeader'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { useDownloadStatusListStore } from '../../../store/downloadstatuslistdialog.store'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import { toast } from 'react-toastify'
import { DrawerDocumentViewer } from '../../common/DocumentViewer'
import CodeButton from '../../common/CodeButton'
import ConfirmDialog from '../../common/ConfirmDialog'
import { useStudyMaterialStore } from '../../../store/studymaterial.store'
import CustomTable from '../../common/custom-table/CustomTable'

const StudyMaterialViewer = ( { onAfterDeleteMaterial, handleClose, openState, studyMaterial } ) => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [attachments, setAttachments] = useState( [] )
    const [previewFile, setPreviewFile] = useState( null )
    const [deletingMaterial, setDeletingMaterial] = useState( false )
    const [deletingAttachment, setDeletingAttachment] = useState( false )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [materialDeleteDialogState, setMaterialDeleteDialogState] = useState( false )
    const [attachmentSelectedForDeletion, setAttachmentSelectedForDeletion] = useState( null )

    const { table, palette, border, layout } = useTheme()

    const DownloadStatusListStore = useDownloadStatusListStore()
    const StudyMaterialStore = useStudyMaterialStore()

    const columns = useMemo( () => [
        {
            name: "Name", padding: "10px 20px", field: "study_material_attachment_title", renderCell: row => (
                <Typography display="flex" gap="5px" alignItems="center" fontSize="12px">
                    <img style={{ objectFit: "cover" }} width="30px" src={attachmentThumbnails[row.study_material_attachment_type] ? attachmentThumbnails[row.study_material_attachment_type] : attachmentThumbnails["default"]} alt={row.study_material_attachment_type} />
                    <strong>{row.study_material_attachment_title || row.study_material_attachment_name}</strong>
                </Typography>
            )
        },
        { name: "Downloads", align: "center", field: "attachment_download_count", },
        { name: "Size", minWidth: "80px", field: "size", align: "center", padding: "10px 20px", renderCell: row => getFileSizeFromBytes( row.study_material_attachment_meta?.size || 0 ) },
        {
            name: "Actions", align: "right", padding: "10px 20px", field: "actions", renderCell: row => (
                <Box justifyContent="flex-end" display="flex" alignItems="center">
                    <IconButton onClick={() => viewAttachment( row )} size='small' color="greyed">
                        {Icons.small.VisibilityIcon}
                    </IconButton>
                    <IconButton onClick={() => { DownloadStatusListStore.downloadAttachment( row.study_material_attachment_id, row.study_material_attachment_url, row.study_material_attachment_title ? row.study_material_attachment_title : row.study_material_attachment_name, row.study_material_attachment_name.split( "." ).pop() ) }} size='small' color="greyed">
                        {Icons.small.DownloadIcon}
                    </IconButton>
                    <IconButton onClick={() => setAttachmentSelectedForDeletion( row )} size='small' color="greyed">
                        {Icons.small.DeleteIcon}
                    </IconButton>
                </Box>
            )
        },
    ], [] )

    const viewAttachment = async ( attachment ) => {
        try {
            console.log( attachment )
            const { data: { data: url } } = await api.downloadAttachment( attachment.study_material_attachment_id, { url: attachment.study_material_attachment_url } )
            setPreviewFile( convertToCDNLink( url ) )
            setPreviewingFileDetails( attachment )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Couldn't preview the file" )
        }
    }

    const deleteAttachment = async ( id ) => {
        setDeletingAttachment( true )
        try {
            await StudyMaterialStore.deleteAttachment( id )
            fetchAttachments()
            setAttachmentSelectedForDeletion( null )
        } catch ( e ) {
            console.log( e )
            toast.error( e.response && e.response.data && e.response.data.message )
        } finally {
            setDeletingAttachment( false )
        }
    }

    const deleteMaterial = async () => {
        try {
            setDeletingMaterial( true )
            if ( await StudyMaterialStore.deleteMaterial( studyMaterial.study_material_id ) ) {
                handleClose()
                if ( onAfterDeleteMaterial ) {
                    onAfterDeleteMaterial( studyMaterial )
                }
            }

        } catch ( e ) {
            console.log( e )
            toast.error( e.response && e.response.data && e.response.data.message )
        } finally {
            setDeletingMaterial( false )
        }
    }

    const fetchAttachments = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: attachments } } = await api.getAttachments( studyMaterial.study_material_id )
            setAttachments( attachments )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( err?.response?.data?.message || "Opps! An error occured while trying to fetch attchments, please try again." )
        } finally {
            setLoading( false )
        }
    }, [studyMaterial] )

    useEffect( () => {
        fetchAttachments()
    }, [fetchAttachments] )


    return (
        <Dialog open={openState} slotProps={{
            paper: { sx: { width: "95%", maxWidth: "1200px" } }
        }}>
            {/* Material Repo delete confirmation */}
            {materialDeleteDialogState && <ConfirmDialog enableSecondaryConfirmation={!error && attachments.length > 0} secondaryConfirmationText='remove-attachments-and-delete' status={materialDeleteDialogState} waiting={deletingMaterial} confirmAction={deleteMaterial} cancelAction={() => setMaterialDeleteDialogState( false )} actionName="Delete" message={<p variant="body2">Do you really want to delete <strong><em> <u>{studyMaterial?.study_material_name}</u> </em></strong>. All the attachments will be deleted!</p>} />}

            {/* Material Attachment delete confirmation */}
            {Boolean( attachmentSelectedForDeletion ) && <ConfirmDialog status={Boolean( attachmentSelectedForDeletion )} waiting={deletingAttachment} confirmAction={() => deleteAttachment( attachmentSelectedForDeletion?.study_material_attachment_id )} cancelAction={() => setAttachmentSelectedForDeletion( null )} actionName="Delete" message={<p variant="body2">Do you really want to delete <strong><em> <u>{attachmentSelectedForDeletion.study_material_attachment_name}</u> </em></strong>.</p>} />}

            {/* In-app Viewer */}
            <DrawerDocumentViewer setPreviewFile={setPreviewFile} setPreviewingFileDetails={setPreviewingFileDetails} fileName={previewingFileDetails?.study_material_attachment_title || previewingFileDetails?.study_material_attachment_name} loadingText="Loading material..." setFile={setPreviewFile} type={previewingFileDetails?.type || previewingFileDetails?.study_material_attachment_name?.split( "." ).pop()} file={previewFile} />

            {/* Header */}
            <Box borderBottom={border[1]} display="flex" alignItems="flex-start" justifyContent="space-between" padding="20px" gap="20px" >
                <Box flexGrow={1}>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>{studyMaterial.subject_code} - {studyMaterial.subject_name}</Typography>
                    <Typography variant="h1" fontWeight="600" component="h2" fontSize="16px">{studyMaterial.study_material_name}</Typography>
                    <Typography gutterBottom variant="subtitle2">By {studyMaterial.study_material_created_by}</Typography>
                    <Box marginTop="10px" display="flex" gap="10px" alignItems="center" flexWrap="wrap" color='white' >{studyMaterial.study_material_tags?.split( "," )?.map( ( tag, tagIndex ) => ( <Typography fontSize="12px" component="span" border={border[1]} bgcolor={palette.contentBg} padding="5px 10px" borderRadius={layout.borderRadius} key={tagIndex}>{tag?.trim()}</Typography> ) )}</Box>

                    <Box marginTop={layout.sectionsGap} border={border[1]} borderRadius={layout.borderRadius} padding="10px" display="flex" flexDirection="column" >
                        <Typography color='error' fontWeight="600">Danger Zone</Typography>
                        <Typography variant='subtitle2' fontSize="12px" gutterBottom display="block" color='textSecondary'>Delete this study material and all of its attachments.</Typography>
                        <Button onClick={() => setMaterialDeleteDialogState( true )} sx={{ textTransform: "capitalize", width: "fit-content", marginTop: "10px" }} startIcon={Icons.default.DeleteIcon} color="errorMessage" variant='contained' disableElevation>Delete Material</Button>
                    </Box>

                </Box>
                <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
            </Box>


            <Box padding="20px" overflow="hidden" display="flex" flexDirection="column">
                {!loading && !error && attachments.length > 0 && <CustomTable tableKey="asmal" columns={columns} data={attachments} datadataIdKey="study_material_attachment_id" pageSize={50} />}
                {!loading && !error && attachments.length === 0 && <Box>
                    <Typography padding="30px" bgcolor={palette.contentBg} borderRadius="10px" color="textSecondary" textAlign="center">No attachments in this study material folder!</Typography>
                </Box>}
            </Box>
            {loading && !error && <DataLoadingSpinner waitingMessage="Please wait while we load the attachments..." />}
            {error && <TryAgainMessageBlock err={error} loading={loading} getData={fetchAttachments} />}
        </Dialog >
    )
}

export default StudyMaterialViewer
